import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, CreditCard, Hash, Landmark, ShieldCheck, Trash2, UserRound } from "lucide-react";
import API from "../services/api";
import BottomNav from "../components/BottomNav";

function BankAccounts() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    bank_name: "",
    account_holder: "",
    account_number: "",
    ifsc: "",
  });

  const loadAccounts = useCallback(async () => {
    if (!user?.id) return;
    const res = await API.get(`/bank-accounts/${user.id}`);
    setAccounts(Array.isArray(res.data) ? res.data : []);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }
    loadAccounts();
  }, [loadAccounts, navigate, user?.id]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveAccount = async () => {
    if (!form.bank_name || !form.account_holder || !form.account_number || !form.ifsc) {
      alert("Enter complete bank details");
      return;
    }

    await API.post("/bank-accounts", {
      ...form,
      user_id: user.id,
      is_default: true,
    });

    setForm({ bank_name: "", account_holder: "", account_number: "", ifsc: "" });
    loadAccounts();
  };

  const deleteAccount = async (id) => {
    await API.delete(`/bank-accounts/${id}`);
    loadAccounts();
  };

  return (
    <div style={page}>
      <header style={topbar}>
        <button type="button" style={backButton} onClick={() => navigate("/profile")}>
          <ArrowLeft size={22} />
        </button>
        <strong style={headerTitle}>Bank Accounts</strong>
      </header>

      <main style={content}>
        <section style={card}>
          <h2 style={title}>Add Bank Account</h2>
          <p style={subtitle}>Enter details for instant withdrawals</p>

          <div style={fieldsCard}>
            <Field
              icon={<Building2 size={20} />}
              label="Bank Name"
              placeholder="Select your bank"
              value={form.bank_name}
              onChange={(value) => updateField("bank_name", value)}
            />
            <Field
              icon={<UserRound size={20} />}
              label="Account Holder Name"
              placeholder="As per bank records"
              value={form.account_holder}
              onChange={(value) => updateField("account_holder", value)}
            />
            <Field
              icon={<CreditCard size={20} />}
              label="Account Number"
              placeholder="Enter account number"
              value={form.account_number}
              onChange={(value) => updateField("account_number", value)}
            />
            <Field
              icon={<Hash size={20} />}
              label="IFSC Code"
              placeholder="E.G., SBIN0001234"
              value={form.ifsc}
              onChange={(value) => updateField("ifsc", value.toUpperCase())}
            />
          </div>

          <button type="button" style={saveButton} onClick={saveAccount}>
            Save Bank Details
          </button>
        </section>

        <section style={card}>
          <h3 style={savedTitle}>Saved Details</h3>
          {accounts.length === 0 ? (
            <p style={empty}>No bank details saved yet</p>
          ) : (
            <div style={savedList}>
              {accounts.map((account) => (
                <article key={account.id} style={savedCard}>
                  <div style={savedTop}>
                    <div style={savedHeaderLeft}>
                      <span style={savedBankBadge}>
                        <Landmark size={18} />
                      </span>
                      <div>
                        <strong style={savedBank}>{account.bank_name}</strong>
                        <div style={savedHolder}>{account.account_holder}</div>
                      </div>
                    </div>
                    <button type="button" style={deleteButton} onClick={() => deleteAccount(account.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div style={savedDetailsGrid}>
                    <div style={savedDetailCard}>
                      <span style={savedDetailLabel}>Account Number</span>
                      <strong style={savedDetailValue}>{maskAccount(account.account_number)}</strong>
                    </div>
                    <div style={savedDetailCard}>
                      <span style={savedDetailLabel}>IFSC Code</span>
                      <strong style={savedDetailValue}>{account.ifsc}</strong>
                    </div>
                  </div>
                  <div style={savedFooter}>
                    <span style={defaultBadge}>
                      <ShieldCheck size={14} />
                      Withdrawal Ready
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <BottomNav />
    </div>
  );
}

function Field({ icon, label, placeholder, value, onChange }) {
  return (
    <label style={fieldWrap}>
      <div style={fieldLabelRow}>
        <span style={fieldIcon}>{icon}</span>
        <span style={fieldLabel}>{label}</span>
      </div>
      <input style={fieldInput} placeholder={placeholder} value={value || ""} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function maskAccount(value) {
  const clean = String(value || "");
  if (clean.length <= 4) return clean;
  return `${"*".repeat(Math.max(0, clean.length - 4))}${clean.slice(-4)}`;
}

const page = { minHeight: "100vh", background: "#f8fafc", paddingBottom: "86px" };
const topbar = { width: "min(520px, 100%)", height: "56px", margin: "0 auto", display: "grid", gridTemplateColumns: "42px 1fr", alignItems: "center", background: "#fff", padding: "0 16px", boxSizing: "border-box", borderBottom: "1px solid #e5e7eb" };
const backButton = { border: 0, background: "transparent", color: "#334155", display: "grid", placeItems: "center" };
const headerTitle = { fontSize: "18px", fontWeight: 800, color: "#0f172a" };
const content = { width: "min(520px, 100%)", margin: "0 auto", padding: "16px", boxSizing: "border-box", display: "grid", gap: "16px" };
const card = { background: "#fff", borderRadius: "24px", border: "1px solid #e5e7eb", overflow: "hidden" };
const title = { margin: 0, padding: "18px 18px 0", fontSize: "18px", fontWeight: 800, color: "#0f172a" };
const subtitle = { margin: "6px 0 0", padding: "0 18px 18px", color: "#64748b", fontSize: "14px", borderBottom: "1px solid #f1f5f9" };
const fieldsCard = { padding: "18px", display: "grid", gap: "18px" };
const fieldWrap = { display: "grid", gap: "12px" };
const fieldLabelRow = { display: "flex", alignItems: "center", gap: "12px" };
const fieldIcon = { color: "#94a3b8", display: "grid", placeItems: "center" };
const fieldLabel = { fontSize: "15px", fontWeight: 700, color: "#1e293b" };
const fieldInput = { width: "100%", height: "76px", border: "1px solid #dbe3ef", borderRadius: "20px", padding: "0 18px", boxSizing: "border-box", fontSize: "16px", color: "#111827", background: "#fff" };
const saveButton = { width: "calc(100% - 36px)", margin: "0 18px 18px", height: "52px", border: 0, borderRadius: "18px", background: "#316ce8", color: "#fff", fontWeight: 800, fontSize: "16px" };
const savedTitle = { margin: 0, padding: "18px", fontSize: "17px", fontWeight: 800, color: "#0f172a", borderBottom: "1px solid #f1f5f9" };
const empty = { color: "#94a3b8", textAlign: "center", padding: "24px 18px" };
const savedList = { padding: "18px", display: "grid", gap: "12px" };
const savedCard = { border: "1px solid #e5e7eb", borderRadius: "20px", padding: "16px", display: "grid", gap: "12px", background: "#fff", boxShadow: "0 8px 24px rgba(15,23,42,0.04)" };
const savedTop = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" };
const savedHeaderLeft = { display: "flex", alignItems: "center", gap: "12px", minWidth: 0 };
const savedBankBadge = { width: "42px", height: "42px", borderRadius: "14px", display: "grid", placeItems: "center", background: "linear-gradient(135deg, #eff6ff, #dbeafe)", color: "#2563eb", flexShrink: 0 };
const savedBank = { color: "#0f172a", fontSize: "15px" };
const savedHolder = { color: "#64748b", fontSize: "13px", marginTop: "4px" };
const savedDetailsGrid = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" };
const savedDetailCard = { borderRadius: "16px", border: "1px solid #e2e8f0", background: "#f8fafc", padding: "12px" };
const savedDetailLabel = { display: "block", color: "#94a3b8", fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "6px" };
const savedDetailValue = { color: "#0f172a", fontSize: "14px", fontWeight: 800, wordBreak: "break-word" };
const savedFooter = { display: "flex", justifyContent: "flex-start" };
const defaultBadge = { display: "inline-flex", alignItems: "center", gap: "6px", minHeight: "30px", padding: "0 12px", borderRadius: "999px", background: "#ecfdf5", color: "#059669", fontSize: "12px", fontWeight: 800 };
const deleteButton = { width: "36px", height: "36px", border: 0, borderRadius: "12px", background: "#fef2f2", color: "#dc2626", display: "grid", placeItems: "center", flexShrink: 0 };

export default BankAccounts;
